import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Card, CardContent, Typography, Chip, CircularProgress, Box, Stack, Tooltip, Divider, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { Star, Wifi, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { getCourses } from "../../api/courses";
import api from "../../api/axios";
import SectionHeading from "../../components/ui/SectionHeading";
import { fadeUp } from "../../utils/animations";

/* ------------------------------------------------------------------ */
/*  card_image_url comes back as a relative path like                  */
/*  "/media/courses/cards/business-software.png". Rendered directly,  */
/*  that resolves against the React dev server's own origin (not the  */
/*  API server), so it 404s and the <img> never loads. Resolve it     */
/*  against the API's actual origin instead.                          */
/* ------------------------------------------------------------------ */
const API_ORIGIN = new URL(api.defaults.baseURL, window.location.origin).origin;

function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  return `${API_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

// Label → gradient + emoji, used only when there's no image at all
const LABEL_META = {
  CLOUD:    { gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)", emoji: "☁️" },
  AZURE:    { gradient: "linear-gradient(135deg, #EFF6FF, #BFDBFE)", emoji: "🔷" },
  SECURITY: { gradient: "linear-gradient(135deg, #FFF1F2, #FFE4E6)", emoji: "🔒" },
  DEVOPS:   { gradient: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", emoji: "⚙️" },
  DEFAULT:  { gradient: "linear-gradient(135deg, #F5F3FF, #EDE9FE)", emoji: "📘" },
};

const LEVEL_COLOR = {
  BEGINNER:     { bg: "rgba(5,150,105,0.1)", fg: "#059669" },
  INTERMEDIATE: { bg: "rgba(217,119,6,0.1)", fg: "#D97706" },
  PROFESSIONAL: { bg: "rgba(220,38,38,0.1)", fg: "#DC2626" },
  ADVANCED:     { bg: "rgba(220,38,38,0.1)", fg: "#DC2626" },
};

// Fixed dimensions — identical to RoadmapSection / TrainingSection so all
// three grids line up pixel-for-pixel wherever they sit on the page.
const CARD_HEIGHT = 470;
const CARD_GAP = 24; // px, keep in sync with the gap set on the track below

function formatPrice(price, discountPrice) {
  const fmt = (v) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
  if (discountPrice) {
    return (
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography fontWeight={800} fontSize={15} color="#0B1530">{fmt(discountPrice)}</Typography>
        <Typography fontSize={12} color="text.disabled" sx={{ textDecoration: "line-through" }}>{fmt(price)}</Typography>
      </Stack>
    );
  }
  return <Typography fontWeight={800} fontSize={15} color="#0B1530">{fmt(price)}</Typography>;
}

function StarRating({ rating }) {
  const val = parseFloat(rating) || 0;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Star size={13} fill={val > 0 ? "#F59E0B" : "none"} color={val > 0 ? "#F59E0B" : "#CBD5E1"} />
      <Typography fontSize={12} fontWeight={600} color={val > 0 ? "#334155" : "#94A3B8"}>
        {val > 0 ? val.toFixed(1) : "No ratings"}
      </Typography>
    </Stack>
  );
}

function CourseThumb({ src, alt, label }) {
  const [failed, setFailed] = useState(!src);
  const meta = LABEL_META[label] ?? LABEL_META.DEFAULT;

  return (
    <Box
      className="card-media"
      sx={{
        width: "100%",
        aspectRatio: "16 / 9",     // matches your uploads, so nothing is cropped
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        background: failed ? meta.gradient : "#0B2A5B",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!failed && src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          className="course-thumb-img"
          sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      ) : (
        <Typography fontSize={44}>{meta.emoji}</Typography>
      )}
    </Box>
  );
}

// Same rounded-rectangle CTA as ViewPathButton / EnrollButton.
function ViewCourseButton() {
  return (
    <Box
      className="course-view-btn"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        width: "100%",
        py: 1.35,
        borderRadius: "12px",
        fontWeight: 700,
        fontSize: 14.5,
        color: "white",
        background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
        boxShadow: "0 10px 22px -10px rgba(37,99,235,0.6)",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease",
      }}
    >
      View course
      <ArrowRight size={16} className="course-view-arrow" style={{ transition: "transform 0.2s ease" }} />
    </Box>
  );
}

// A single featured-course card. Unchanged from the grid version — just no
// longer owns its own width (that's set by the motion.div slot in the track).
function FeaturedCourseCard({ c }) {
  const priceContent = formatPrice(c.price, c.discount_price);

  return (
    <Card
      component="a"
      href={`/courses/${c.slug}`}
      className="featured-course-carousel-card"
      sx={{
        height: CARD_HEIGHT,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        border: "1px solid rgba(15,23,42,0.09)",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        scrollSnapAlign: "start",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 16px 32px -20px rgba(15,23,42,0.22)",
          borderColor: "rgba(15,23,42,0.13)",
        },
        "&:hover .course-title": {
          color: "#2563EB",
          textDecoration: "underline",
          textDecorationColor: "rgba(37,99,235,0.35)",
          textUnderlineOffset: "3px",
        },
        "&:hover .course-view-btn": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 28px -10px rgba(37,99,235,0.75)",
          background: "linear-gradient(135deg, #2E6EF0 0%, #2050E0 100%)",
        },
        "&:hover .course-view-arrow": { transform: "translateX(3px)" },
      }}
    >
      <CourseThumb src={resolveMediaUrl(c.card_image_url)} alt={c.title} label={c.label} />

      {/* body: accent rail + content column, matching Roadmap/Training */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <CardContent
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateRows: "30px 44px 40px 1fr auto",
            rowGap: 0,
            pl: 2.25,
            pr: 2.5,
            pt: 2.25,
            pb: 2.5,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            "&:last-child": { pb: 2.5 },
          }}
        >
          {/* Row 1: badges — exactly 30px, always */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "nowrap", overflow: "visible" }}>
            {c.label && (
              <Chip
                label={c.label}
                size="small"
                sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", flexShrink: 0 }}
              />
            )}
            {c.level && (
              <Chip
                label={c.level}
                size="small"
                sx={{
                  height: 20, fontSize: 11, fontWeight: 700, flexShrink: 0,
                  bgcolor: (LEVEL_COLOR[c.level] ?? { bg: "rgba(15,23,42,0.05)" }).bg,
                  color: (LEVEL_COLOR[c.level] ?? { fg: "#475569" }).fg,
                }}
              />
            )}
          </Stack>

          {/* Row 2: title — exactly 44px, always */}
          <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            <Tooltip title={c.title} arrow placement="top" enterDelay={400}>
              <Typography
                className="course-title"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#0B1530",
                  lineHeight: "22px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  width: "100%",
                  wordBreak: "break-word",
                  transition: "color 0.2s ease",
                }}
              >
                {c.title}
              </Typography>
            </Tooltip>
          </Box>

          {/* Row 3: description — exactly 40px, always */}
          <Box sx={{ overflow: "hidden" }}>
            <Tooltip title={c.short_description || ""} arrow placement="top" enterDelay={400}>
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: "#64748B",
                  lineHeight: "20px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  width: "100%",
                  wordBreak: "break-word",
                }}
              >
                {c.short_description || ""}
              </Typography>
            </Tooltip>
          </Box>

          {/* Row 4: flexible spacer */}
          <Box />

          {/* Row 5: footer — natural height, identical on every card */}
          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
              <StarRating rating={c.rating} />
              {c.mode && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Wifi size={13} color="#64748B" />
                  <Typography fontSize={12} color="#64748B" fontWeight={600}>{c.mode}</Typography>
                </Stack>
              )}
            </Stack>
            {priceContent && (
              <Box sx={{ mb: 1.25 }}>{priceContent}</Box>
            )}
            <ViewCourseButton />
          </Box>
        </CardContent>
      </Box>
    </Card>
  );
}

export default function FeaturedCourses() {
  const { data: rawCourses = [], isLoading } = useQuery({
  queryKey: ["featured-courses"],
  queryFn: () => getCourses({ featured: true }).then((r) => r.data),
});

// Sort by order_on_homepage (ascending). Missing values go last.
// Array.sort is stable, so courses with the same order keep the API's
// relative order; id is the explicit tie-breaker.
const courses = useMemo(() => {
  const orderOf = (c) =>
    c.order_on_homepage ?? Number.MAX_SAFE_INTEGER;
  return [...rawCourses].sort(
    (a, b) => orderOf(a) - orderOf(b) || a.id - b.id
  );
}, [rawCourses]);

  // ---- Carousel state/logic, same pattern as CourseGrid / RoadmapSection ----
  const trackRef = useRef(null);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const cardsPerView = isXs ? 1 : isSm ? 2 : isMd ? 3 : 4;

  const [activePage, setActivePage] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Width so exactly `cardsPerView` cards + gaps fill the track, with no
  // partial card poking past the edge. Applied ONLY on the motion.div
  // (direct flex child of the track) — never inside FeaturedCourseCard itself.
  const cardWidthCss = `calc((100% - ${CARD_GAP * (cardsPerView - 1)}px) / ${cardsPerView})`;

  const totalPages = Math.max(1, Math.ceil(courses.length / cardsPerView));

  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
    setActivePage(0);
     setAtStart(true);
  setAtEnd(totalPages <= 1);  
  }, [cardsPerView, courses.length]);

  const scrollByPage = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.querySelector(".featured-course-carousel-card");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth / cardsPerView;
    const step = (cardWidth + CARD_GAP) * cardsPerView;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  }, [cardsPerView]);

  const handleTrackScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 2);
    setAtEnd(track.scrollLeft >= max - 2);
    if (track.clientWidth) {
      const page = Math.round(track.scrollLeft / track.clientWidth);
      setActivePage(Math.max(0, Math.min(page, totalPages - 1)));
    }
  }, [totalPages]);

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box component="section" sx={{ bgcolor: "#F1F5F9", pt: 12, pb: 0 }}>
      <div className="section-shell">
        <SectionHeading
          eyebrow="Featured courses"
          title="Premium certification programs"
          copy="Certification programs with instructors, ratings, labs and enrollment."
        />

        {courses.length === 0 ? (
          <Box textAlign="center" mt={6} color="text.secondary">
            <Typography>No featured courses available right now.</Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={trackRef}
              onScroll={handleTrackScroll}
              className="featured-course-carousel-track"
              sx={{
                mt: 4,
                display: "flex",
                gap: `${CARD_GAP}px`,
                overflowX: "auto",
                overflowY: "hidden",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                pb: 1.5,
                px: { xs: 0.5, md: 0 },
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {courses.map((c, i) => (
                <motion.div
                  key={c.id}
                  {...fadeUp(i * 0.06)}
                  style={{
                    display: "block",
                    flex: `0 0 ${cardWidthCss}`,
                    width: cardWidthCss,
                  }}
                >
                  <FeaturedCourseCard c={c} />
                </motion.div>
              ))}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 3, pb: 5 }}>
              <IconButton
                onClick={() => scrollByPage(-1)}
                disabled={atStart}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0B1530", minWidth: 64, textAlign: "center" }}>
                {String(activePage + 1).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
              </Typography>

              <IconButton
                onClick={() => scrollByPage(1)}
                disabled={atEnd}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>
          </>
        )}
      </div>
    </Box>
  );
}